import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { ReactElement, useEffect, useMemo } from 'react';
import styles from './AssetSelectionTable.module.scss';
import { lockColumn } from '@customer/components/tables/grid/columnDefs';
import AssetGroupCellRenderer from '@customer/components/tables/cells/asset-group/AssetGroupCellRenderer';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useAssetGroupIdFormatter } from '@customer/components/tables/formatters/idFormatters';
import { useTranslation } from 'react-i18next';
import SearchBox from '@customer/components/inputs/search-box/SearchBox';
import classNames from 'classnames';
import { useController, UseControllerProps } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import {
  register,
  resetTable,
  selectGridData,
  setSelectedRows,
} from '@customer/store/slices/gridSlice';
import { GridState } from '@dmc/ui/tables';
import { getObjectType } from '@cas/utils/translationHelpers';
import { AssetBasicModel } from '@customer/store/apis/assetsApi';
import { ObjectTypeId } from '@dmc/ui';
import { CustomInvestigationInputFields } from '../custom-investigation-form/CustomInvestigationForm';

type Model = AssetBasicModel;

const defaultSliceKey = 'AssetTracksForm--Create';

const defaultColDef: ColDef<Model> = {
  ...lockColumn,
  sortable: true,
};

interface AssetSelectionTableProps {
  control: UseControllerProps<CustomInvestigationInputFields>['control'];
  assets: Model[] | undefined;
  maxSelection: number;
  defaultSelection: number[];
  sliceKey?: string;
}

export const AssetSelectionTable = ({
  control,
  assets,
  maxSelection,
  defaultSelection,
  sliceKey = defaultSliceKey,
}: AssetSelectionTableProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const assetGroupIdFormatter = useAssetGroupIdFormatter();

  const columnDefs = useMemo<ColDef<Model>[]>(
    () => [
      {
        field: 'name',
        headerName: t('Field.AssetName.Label'),
        minWidth: 110,
        maxWidth: 130,
      },
      {
        field: 'obj',
        headerName: t('Field.ObjectType.Label'),
        minWidth: 150,
        valueGetter: ({ data }: ValueGetterParams<Model>): string =>
          getObjectType(t, data?.obj ?? ObjectTypeId.Unknown),
      },
      {
        field: 'ags',
        headerName: t('Field.AssetGroup.Label'),
        valueFormatter: assetGroupIdFormatter<Model, 'ags'>,
        cellRenderer: AssetGroupCellRenderer,
        minWidth: 150,
      },
    ],
    [t, assetGroupIdFormatter],
  );

  // RTK entity adapters incorrectly assert that the state is always defined for any ID that is provided.
  const gridState = useAppSelector(state => selectGridData(state, sliceKey)) as
    | GridState
    | undefined;

  const selectedRows = useMemo<string[]>(
    () => gridState?.selectedRows ?? defaultSelection.map(String),
    [gridState?.selectedRows, defaultSelection],
  );

  const { field, fieldState } = useController({
    control,
    name: 'assetIds',
  });

  // Update the 'assetIds' field when the table row selection changes.
  useEffect(() => {
    field.onBlur();
    field.onChange(selectedRows.map(Number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  useEffect(() => {
    // Register the table slice early if there is a default selection.
    if (defaultSelection.length) {
      dispatch(register(sliceKey));
      // Load the slice with the investigation's existing asset selection.
      dispatch(setSelectedRows({ id: sliceKey, value: defaultSelection.map(String) }));
    }
    // Cleanup the table for next use.
    return () => {
      dispatch(resetTable(sliceKey));
    };
  }, [dispatch, sliceKey, defaultSelection]);

  const errorMessage = fieldState.error ? fieldState.error.message : undefined;

  let fieldMessage = errorMessage;

  if (!errorMessage) {
    const remaining = maxSelection - selectedRows.length;
    const countHelper = t('Validation.LimitRemaining', {
      field: 'Asset',
      count: maxSelection,
      remaining,
    });
    const limitReachedHelper = t('Validation.LimitReached', {
      field: 'Asset',
      count: maxSelection,
    });
    fieldMessage = remaining > 0 ? countHelper : limitReachedHelper;
  }

  return (
    <div className={styles.container}>
      <div
        className={classNames(styles.tableField, { [styles['tableField--error']]: errorMessage })}
      >
        <SearchBox sliceKey={sliceKey} />
        <GridControlled<Model>
          sliceKey={sliceKey}
          rowData={assets}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelectMode="multiRow"
          // Turn on persistence so that the default selection value is read from the store on mount.
          persistRowSelection={defaultSelection.length > 0}
        />
      </div>
      <div className={classNames(styles.message, { [styles['message--error']]: errorMessage })}>
        {fieldMessage}
      </div>
    </div>
  );
};
